import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Card, CardBody, Badge, Button } from "reactstrap";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";
import Spinners from "../../components/Common/Spinner";
import { useGetAdminDashboard } from "../../api/dashboard";
import { withTranslation } from "react-i18next";

/**
 * Full-width SVG bar chart (no external lib).
 * Fix: SVG now fills the available width (no big empty space),
 * by using width:100% + height:auto and no fixed container height.
 */
const SvgBarChart = ({ labels, values }) => {
  const cfg = useMemo(() => {
    const maxVal = Math.max(...(values?.length ? values : [0]), 0);

    const getNiceScale = (max) => {
      if (max <= 10) return { yMax: 10, ticks: [0, 2, 4, 6, 8, 10] };
      if (max <= 20) return { yMax: 20, ticks: [0, 5, 10, 15, 20] };
      if (max <= 50) return { yMax: 50, ticks: [0, 10, 20, 30, 40, 50] };
      if (max <= 100) return { yMax: 100, ticks: [0, 20, 40, 60, 80, 100] };

      const roundedMax = Math.ceil(max / 50) * 50;
      const step = roundedMax / 5;
      return {
        yMax: roundedMax,
        ticks: [0, step, step * 2, step * 3, step * 4, step * 5]
      };
    };

    const { yMax, ticks } = getNiceScale(maxVal);
    return { yMax: yMax || 1, ticks: ticks?.length ? ticks : [0, 1], maxVal };
  }, [values]);

  // Virtual SVG layout
  const W = 1200;
  const H = 320;

  const margin = { top: 18, right: 24, bottom: 56, left: 60 };
  const plotW = W - margin.left - margin.right;
  const plotH = H - margin.top - margin.bottom;

  const barCount = labels?.length || 12;
  const groupW = plotW / Math.max(barCount, 1);
  const barW = Math.max(groupW * 0.6, 10);

  const yToPx = (v) => {
    const vv = Math.max(0, Number(v) || 0);
    return margin.top + plotH - (vv / cfg.yMax) * plotH;
  };

  return (
    <div style={{ width: "100%" }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        preserveAspectRatio="xMinYMin meet"
        role="img"
        aria-label="Monthly users bar chart"
      >
        {/* Grid + Y labels */}
        {cfg.ticks.map((t) => {
          const y = yToPx(t);
          return (
            <g key={`tick-${t}`}>
              <line
                x1={margin.left}
                x2={W - margin.right}
                y1={y}
                y2={y}
                stroke="#e9ecef"
                strokeWidth="2"
              />
              <text x={margin.left - 12} y={y + 5} textAnchor="end" fontSize="16" fill="#6c757d">
                {Number.isInteger(t) ? t : Math.round(t)}
              </text>
            </g>
          );
        })}

        {/* Bars + X labels */}
        {(values || []).map((val, i) => {
          const xCenter = margin.left + groupW * i + groupW / 2;
          const x = xCenter - barW / 2;
          const y = yToPx(val);
          const h = margin.top + plotH - y;

          const label = labels?.[i] ?? "";
          const vNum = Math.max(0, Number(val) || 0);

          return (
            <g key={`bar-${i}`}>
              <rect x={x} y={y} width={barW} height={h} rx="8" ry="8" fill="#0D6EFD" />

              {vNum > 0 && (
                <text
                  x={xCenter}
                  y={y - 10}
                  textAnchor="middle"
                  fontSize="16"
                  fill="#212529"
                  fontWeight="600"
                >
                  {vNum}
                </text>
              )}

              <text
                x={xCenter}
                y={margin.top + plotH + 32}
                textAnchor="middle"
                fontSize="16"
                fill="#6c757d"
              >
                {label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

SvgBarChart.propTypes = {
  labels: PropTypes.array,
  values: PropTypes.array
};

const Dashboard = (props) => {
  const { t } = props;
  document.title = "Admin Dashboard | JobArk";

  const [filter, setFilter] = useState("year");
  const { mutate, data, isLoading, isPending } = useGetAdminDashboard();
  const loading = isLoading || isPending;

  useEffect(() => {
    mutate({ filter });
  }, [filter, mutate]);

  const dashboardData = data?.data || {};
  const {
    pendingUsersCount = 0,
    onboardingData = [],
    pendingBookingsCount = 0,
    activeBookingsCount = 0,
    payoutToWorker = 0,
    totalEarnings = 0,
    recentBookings = [],
    recentContactUs = []
  } = dashboardData;

  const safeOnboardingData = Array.isArray(onboardingData) ? onboardingData : [];

  const currentMonth = new Date().toLocaleString("default", { month: "long" });
  const currentMonthData = safeOnboardingData.find((item) => item?.month === currentMonth);
  const newBookingsCount = currentMonthData?.userCount || 0;

  const chartData = useMemo(() => {
    const allMonths = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December"
    ];

    const dataMap = new Map();
    safeOnboardingData.forEach((item) => {
      if (item?.month) dataMap.set(item.month, Number(item.userCount) || 0);
    });

    const labels = allMonths.map((m) => m.slice(0, 3));
    const values = allMonths.map((m) => dataMap.get(m) || 0);

    return { labels, values };
  }, [safeOnboardingData]);

  const recentActivity = useMemo(() => {
    const activities = [];

    if (Array.isArray(recentBookings)) {
      recentBookings.forEach((booking) => {
        if (!booking) return;
        activities.push({
          id: booking.id || `booking-${Math.random()}`,
          type: "booking",
          client: booking.client?.fullName || "Unknown",
          worker: booking.worker?.fullName || null,
          activity: "Created new booking",
          time: booking.createdAt || new Date().toISOString(),
          status: booking.status || "new"
        });
      });
    }

    if (Array.isArray(recentContactUs)) {
      recentContactUs.forEach((contact) => {
        if (!contact) return;
        activities.push({
          id: contact.id || `contact-${Math.random()}`,
          type: "contact",
          client: contact.user?.fullName || "Unknown",
          worker: null,
          activity: `Contact: ${contact.subject || "General Inquiry"}`,
          time: contact.createdAt || new Date().toISOString(),
          status: contact.status || "new"
        });
      });
    }

    try {
      return activities
        .sort((a, b) => {
          const dateA = a.time ? new Date(a.time).getTime() : 0;
          const dateB = b.time ? new Date(b.time).getTime() : 0;
          return dateB - dateA;
        })
        .slice(0, 5);
    } catch {
      return activities.slice(0, 5);
    }
  }, [recentBookings, recentContactUs]);

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "Unknown";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? "min" : "mins"} ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
    if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
    return date.toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const statusLower = (status || "").toLowerCase();
    if (statusLower === "new" || statusLower === "pending")
      return <Badge className="bg-primary">New</Badge>;
    if (statusLower === "assigned" || statusLower === "active")
      return <Badge className="bg-success">Assigned</Badge>;
    if (statusLower === "paid" || statusLower === "completed")
      return <Badge className="bg-warning">Paid</Badge>;
    if (statusLower === "contacted") return <Badge className="bg-info">Contacted</Badge>;
    if (statusLower === "solved") return <Badge className="bg-success">Solved</Badge>;
    return <Badge className="bg-secondary">{status || "Unknown"}</Badge>;
  };

  const activityColumns = useMemo(
    () => [
      {
        header: "Client / Worker",
        accessorKey: "client",
        enableColumnFilter: false,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="d-flex align-items-center">
              <div
                className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-2"
                style={{ width: "32px", height: "32px", fontSize: "12px" }}
              >
                {row.client?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <div>
                <div className="fw-medium">{row.client || "Unknown"}</div>
                {row.worker && <small className="text-muted">{row.worker}</small>}
              </div>
            </div>
          );
        }
      },
      { header: "Activity", accessorKey: "activity", enableColumnFilter: false },
      {
        header: "Time",
        accessorKey: "time",
        enableColumnFilter: false,
        cell: (info) => formatTimeAgo(info.getValue())
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (info) => getStatusBadge(info.getValue())
      }
    ],
    []
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={t("Dashboards")} breadcrumbItem="Admin Dashboard" />

          <Row className="mb-3">
            <Col>
              <div className="d-flex justify-content-end align-items-center gap-2">
                <Button
                  onClick={() => setFilter("month")}
                  style={{
                    backgroundColor: filter === "month" ? "#0D6EFD" : "#f8f9fa",
                    color: filter === "month" ? "#ffffff" : "#6c757d",
                    border: filter === "month" ? "1px solid #0D6EFD" : "1px solid #dee2e6",
                    padding: "8px 24px",
                    fontWeight: filter === "month" ? "600" : "400",
                    borderRadius: "6px",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    if (filter !== "month") {
                      e.currentTarget.style.backgroundColor = "#e9ecef";
                      e.currentTarget.style.color = "#495057";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (filter !== "month") {
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                      e.currentTarget.style.color = "#6c757d";
                    }
                  }}
                >
                  Month
                </Button>
                <Button
                  onClick={() => setFilter("year")}
                  style={{
                    backgroundColor: filter === "year" ? "#0D6EFD" : "#f8f9fa",
                    color: filter === "year" ? "#ffffff" : "#6c757d",
                    border: filter === "year" ? "1px solid #0D6EFD" : "1px solid #dee2e6",
                    padding: "8px 24px",
                    fontWeight: filter === "year" ? "600" : "400",
                    borderRadius: "6px",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    if (filter !== "year") {
                      e.currentTarget.style.backgroundColor = "#e9ecef";
                      e.currentTarget.style.color = "#495057";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (filter !== "year") {
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                      e.currentTarget.style.color = "#6c757d";
                    }
                  }}
                >
                  Year
                </Button>
              </div>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5">
              <Spinners />
            </div>
          ) : (
            <>
              <Row className="mb-4">
                <Col md={6} lg={3}>
                  <Card className="mb-3">
                    <CardBody>
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center me-3 bg-secondary"
                          style={{
                            width: "48px",
                            height: "48px",
                            color: "white"
                          }}
                        >
                          <i
                            className="mdi mdi-account-alert-outline"
                            style={{ fontSize: "24px" }}
                          />
                        </div>
                        <div className="flex-grow-1">
                          <div className="text-muted small">Pending Users</div>
                          <div className="h4 mb-0">{pendingUsersCount}</div>
                          <small className="text-success">+{pendingUsersCount} pending</small>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col md={6} lg={3}>
                  <Card className="mb-3">
                    <CardBody>
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center me-3 bg-secondary"
                          style={{
                            width: "48px",
                            height: "48px",
                            color: "white"
                          }}
                        >
                          <i className="mdi mdi-folder-outline" style={{ fontSize: "24px" }} />
                        </div>
                        <div className="flex-grow-1">
                          <div className="text-muted small">Pending Bookings</div>
                          <div className="h4 mb-0">{pendingBookingsCount}</div>
                          <small className="text-muted">Awaiting assignment</small>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col md={6} lg={3}>
                  <Card className="mb-3">
                    <CardBody>
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center me-3 bg-secondary"
                          style={{
                            width: "48px",
                            height: "48px",
                            color: "white"
                          }}
                        >
                          <i
                            className="mdi mdi-file-document-outline"
                            style={{ fontSize: "24px" }}
                          />
                        </div>
                        <div className="flex-grow-1">
                          <div className="text-muted small">New Users</div>
                          <div className="h4 mb-0">{newBookingsCount}</div>
                          <small className="text-success">+{newBookingsCount} this month</small>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col md={6} lg={3}>
                  <Card className="mb-3">
                    <CardBody>
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center me-3 bg-secondary"
                          style={{
                            width: "48px",
                            height: "48px",
                            color: "white"
                          }}
                        >
                          <i className="mdi mdi-currency-usd" style={{ fontSize: "24px" }} />
                        </div>
                        <div className="flex-grow-1">
                          <div className="text-muted small">Total Earnings</div>
                          <div className="h4 mb-0">
                            ${Number(totalEarnings || 0).toLocaleString()}
                          </div>
                          <small className="text-muted">Total revenue</small>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col lg={8}>
                  <Card className="mb-3">
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <h5 className="mb-0">Onboarding Overview</h5>
                          <div className="mt-1 d-flex align-items-center gap-2">
                            <span
                              className="rounded-circle"
                              style={{
                                width: 10,
                                height: 10,
                                backgroundColor: "#0D6EFD",
                                display: "inline-block"
                              }}
                            />
                            <small className="text-muted">Users</small>
                          </div>
                        </div>

                        <div className="text-end">
                          <small className="text-muted d-block">
                            {filter === "year" ? "Year view" : "Month view"}
                          </small>
                          <small className="text-muted">
                            Active bookings: <strong>{activeBookingsCount}</strong>
                          </small>
                        </div>
                      </div>

                      <SvgBarChart labels={chartData.labels} values={chartData.values} />
                    </CardBody>
                  </Card>

                  <Card>
                    <CardBody>
                      <h5 className="mb-3">Recent Activity</h5>
                      {recentActivity.length > 0 ? (
                        <TableContainer
                          columns={activityColumns}
                          data={recentActivity}
                          isGlobalFilter={false}
                          isPagination={false}
                        />
                      ) : (
                        <div className="text-center py-4 text-muted">No recent activity</div>
                      )}
                    </CardBody>
                  </Card>
                </Col>

                <Col lg={4}>
                  <Card className="mb-3">
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">Worker Payouts</h5>
                        <Link to="/booking/list" className="text-primary small">
                          View All
                        </Link>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="text-muted small">Total payouts</div>
                        <div className="fw-semibold">
                          ${Number(payoutToWorker || 0).toLocaleString()}
                        </div>
                      </div>

                      <div className="text-muted small mb-3">
                        Recent worker payouts will appear here
                      </div>

                      <div className="text-center py-3 text-muted">
                        <i className="mdi mdi-information-outline me-2"></i>
                        No payouts data available
                      </div>
                    </CardBody>
                  </Card>

                  <Card>
                    <CardBody>
                      <h5 className="mb-3">Quick Actions</h5>
                      <div className="d-flex flex-column gap-2">
                        <Link
                          to="/booking/list"
                          className="d-flex align-items-center justify-content-between p-2 rounded text-decoration-none"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          <div className="d-flex align-items-center">
                            <i className="mdi mdi-magnify me-2 text-primary"></i>
                            <span>View Bookings</span>
                          </div>
                          <i className="mdi mdi-chevron-right text-muted"></i>
                        </Link>

                        <Link
                          to="/user/list"
                          className="d-flex align-items-center justify-content-between p-2 rounded text-decoration-none"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          <div className="d-flex align-items-center">
                            <i className="mdi mdi-checkbox-marked-outline me-2 text-primary"></i>
                            <span>Manage Requests</span>
                          </div>
                          <i className="mdi mdi-chevron-right text-muted"></i>
                        </Link>

                        <Link
                          to="/booking"
                          className="d-flex align-items-center justify-content-between p-2 rounded text-decoration-none"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          <div className="d-flex align-items-center">
                            <i className="mdi mdi-account-multiple me-2 text-primary"></i>
                            <span>Assign Worker</span>
                          </div>
                          <i className="mdi mdi-chevron-right text-muted"></i>
                        </Link>

                        <Link
                          to="/booking/list"
                          className="d-flex align-items-center justify-content-between p-2 rounded text-decoration-none"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          <div className="d-flex align-items-center">
                            <i className="mdi mdi-cash-multiple me-2 text-primary"></i>
                            <span>Send Payout</span>
                          </div>
                          <i className="mdi mdi-chevron-right text-muted"></i>
                        </Link>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

Dashboard.propTypes = {
  t: PropTypes.any
};

export default withTranslation()(Dashboard);
